import React from 'react';
import { BarChart3, Settings } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  Cell 
} from 'recharts';

const GraficoEvolucaoVendas = ({ dadosEvolucao = [], metasDefinidas = {}, podeDefinirMetas, onDefinirMetas }) => {
  // Paleta de cores profissional
  const CORES = {
    vendas: '#4F46E5',     // Índigo vibrante
    metaAtingida: '#10B981', // Verde esmeralda
    metaNaoAtingida: '#EF4444' // Vermelho suave
  };

  // Função para formatar o mês
  const formatarMes = (mes) => {
    const meses = {
      '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
      '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
    };
    
    if (meses[mes]) return meses[mes];
    if (typeof mes === 'string' && mes.includes('-')) {
      const mesNumero = mes.split('-')[1];
      return meses[mesNumero] || mes;
    }
    if (typeof mes === 'number') {
      return meses[mes.toString().padStart(2, '0')] || mes.toString();
    }
    return mes;
  };

  // Processar dados incluindo informações de meta
  const dadosProcessados = dadosEvolucao.map(item => {
    const mesFormatado = formatarMes(item.mes);
    const meta = metasDefinidas[mesFormatado]?.vendas || 0;
    const alcance = meta > 0 ? Math.min(100, Math.round((item.vendas / meta) * 100)) : 0;
    
    return {
      ...item,
      mes: mesFormatado,
      meta,
      alcance,
      status: meta > 0 && item.vendas >= meta ? 'atingido' : 'não-atingido'
    };
  });

  const temMetas = Object.values(metasDefinidas).some(meta => meta.vendas > 0);

  // Customizar conteúdo do Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const vendas = payload.find(p => p.dataKey === 'vendas')?.value || 0;
      const meta = payload.find(p => p.dataKey === 'meta')?.value || 0;
      const status = meta > 0 && vendas >= meta ? '✅ Atingida' : meta > 0 ? '❌ Não atingida' : 'Meta não definida';
      
      return (
        <div className="bg-[color:var(--surface)] p-3 rounded-md shadow-md border border-[color:var(--border)]">
          <p className="font-medium text-[color:var(--text-primary)]">{label}</p>
          <p className="text-[color:var(--accent)]"><span className="font-medium">Vendas:</span> {vendas} contratos</p>
          {/* `--status-good` rende 3,44:1 sobre superfície clara e `rose-600` é escuro demais para o
              tema escuro. As variantes `-strong` foram medidas e passam nos dois. */}
          {meta > 0 && (
            <p className={vendas >= meta ? "text-[color:var(--status-good-strong)]" : "text-[color:var(--status-danger-strong)]"}>
              <span className="font-medium">Meta:</span> {meta} contratos
            </p>
          )}
          <p className="text-sm mt-1">{status}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center whitespace-nowrap">
          <BarChart3 className="h-5 w-5 text-[color:var(--accent)] mr-2 flex-shrink-0" />
          <span className="truncate">Evolução de Vendas vs Meta</span>
        </h3>
        {podeDefinirMetas && (
          <button
            onClick={onDefinirMetas}
            className="p-2 bg-[color:var(--surface-muted)] text-[color:var(--accent)] rounded-lg hover:bg-[color:var(--surface-muted)] transition-colors flex items-center justify-center flex-shrink-0"
            title="Definir Metas"
          >
            <Settings className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="h-64">
        {dadosProcessados.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[color:var(--text-muted)]">
            <p>Nenhum dado disponível</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              barCategoryGap="25%"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis 
                dataKey="mes"
                tick={{ fill: '#4b5563', fontSize: 12, fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fill: '#4b5563', fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom"
                height={40}
                iconType="circle"
                iconSize={10}
                formatter={(value) => {
                  if (value === 'vendas') return 'Vendas Realizadas';
                  if (value === 'meta') return 'Meta Definida';
                  return value;
                }}
                payload={[
                  { value: 'Vendas Realizadas', type: 'circle', color: CORES.vendas },
                  { value: 'Meta Definida', type: 'circle', color: '#9ca3af' }
                ]}
              />
              
              {/* Barra de Vendas - sempre visível */}
              <Bar
                dataKey="vendas"
                name="Vendas"
                fill={CORES.vendas}
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
              
              {/* Barra de Meta (somente se existir meta) */}
              {temMetas && (
                <Bar
                  dataKey="meta"
                  name="Meta"
                  fill="#9ca3af" // Cor neutra para legenda
                  radius={[4, 4, 0, 0]}
                  barSize={8}
                >
                  {dadosProcessados.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.meta > 0
                          ? entry.status === 'atingido'
                            ? CORES.metaAtingida
                            : CORES.metaNaoAtingida
                          : 'transparent'
                      }
                    />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default GraficoEvolucaoVendas;



